import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { getPricing, createPricing, updatePricing, deletePricing, recalculateCosts, getUnknownPricing, getPendingPricing, approvePendingPricing, rejectPendingPricing } from '../services/costs';
import { setTitle } from '../utils/environment';
import PricingForm from './PricingForm';
import AdminGate from '../components/AdminGate';
import PageHeader from '../components/PageHeader';
import PageLoading from '../components/PageLoading';

const PricingManagement = () => {
  const { showError, showSuccess } = useToast();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState([]);
  const [unknownPricing, setUnknownPricing] = useState([]);
  const [pendingPricing, setPendingPricing] = useState([]);
  const [resolvingId, setResolvingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    provider: '',
    model: '',
    input_cost_per_1m: '',
    output_cost_per_1m: '',
    per_request_cost: '',
    effective_from: new Date().toISOString().split('T')[0]
  });

  // Recalculate form state
  const [recalcStartDate, setRecalcStartDate] = useState('');
  const [recalcEndDate, setRecalcEndDate] = useState('');
  const [recalcResult, setRecalcResult] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  /**
   * Load pricing data from API
   */
  const loadData = async () => {
    try {
      setLoading(true);
      const [pricingData, unknownData, pendingData] = await Promise.all([
        getPricing(),
        getUnknownPricing(),
        getPendingPricing()
      ]);
      setPricing(pricingData);
      setUnknownPricing(unknownData);
      setPendingPricing(pendingData);

      // Check URL params for pre-fill
      const provider = searchParams.get('provider');
      const model = searchParams.get('model');
      if (provider && model) {
        setFormData(prev => ({
          ...prev,
          provider,
          model
        }));
        setShowForm(true);
      }
    } catch (error) {
      console.error('Failed to load pricing:', error);
      if (error.response?.status === 403) {
        setForbidden(true);
      } else {
        showError(error.response?.data?.detail || 'Failed to load pricing data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTitle('Pricing · Read It Never');
    loadData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      provider: '',
      model: '',
      input_cost_per_1m: '',
      output_cost_per_1m: '',
      per_request_cost: '',
      effective_from: new Date().toISOString().split('T')[0]
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        provider: formData.provider,
        model: formData.model,
        input_cost_per_1m: formData.input_cost_per_1m ? parseFloat(formData.input_cost_per_1m) : null,
        output_cost_per_1m: formData.output_cost_per_1m ? parseFloat(formData.output_cost_per_1m) : null,
        per_request_cost: formData.per_request_cost ? parseFloat(formData.per_request_cost) : null,
        effective_from: formData.effective_from
      };

      if (editingId) {
        await updatePricing(editingId, data);
        showSuccess('Pricing updated successfully');
      } else {
        await createPricing(data);
        showSuccess('Pricing created successfully');
      }
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to save pricing:', error);
      showError(error.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      provider: item.provider,
      model: item.model,
      input_cost_per_1m: item.input_cost_per_1m || '',
      output_cost_per_1m: item.output_cost_per_1m || '',
      per_request_cost: item.per_request_cost || '',
      effective_from: item.effective_from
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (!(await confirm({
      title: 'Delete pricing entry?',
      message: `Delete ${item.provider} / ${item.model} pricing effective ${item.effective_from}? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;

    try {
      await deletePricing(item.id);
      showSuccess('Pricing deleted successfully');
      loadData();
    } catch (error) {
      console.error('Failed to delete pricing:', error);
      showError(error.response?.data?.detail || 'Failed to delete pricing');
    }
  };

  const handleApproveProposal = async (id) => {
    try {
      setResolvingId(id);
      await approvePendingPricing(id);
      showSuccess('Pricing change approved and applied');
      loadData();
    } catch (error) {
      console.error('Failed to approve pricing change:', error);
      showError(error.response?.data?.detail || 'Failed to approve pricing change');
    } finally {
      setResolvingId(null);
    }
  };

  const handleRejectProposal = async (id) => {
    try {
      setResolvingId(id);
      await rejectPendingPricing(id);
      showSuccess('Pricing change rejected');
      loadData();
    } catch (error) {
      console.error('Failed to reject pricing change:', error);
      showError(error.response?.data?.detail || 'Failed to reject pricing change');
    } finally {
      setResolvingId(null);
    }
  };

  const handleRecalculate = async (e) => {
    e.preventDefault();
    if (!recalcStartDate || !recalcEndDate) {
      showError('Please select both start and end dates');
      return;
    }
    try {
      setRecalculating(true);
      const result = await recalculateCosts(recalcStartDate, recalcEndDate);
      setRecalcResult(result);
      showSuccess(`Recalculated ${result.updated_count} logs`);
    } catch (error) {
      console.error('Failed to recalculate costs:', error);
      showError(error.response?.data?.detail || 'Failed to recalculate costs');
    } finally {
      setRecalculating(false);
    }
  };

  const handleAddFromUnknown = (item) => {
    setFormData({
      provider: item.provider,
      model: item.model,
      input_cost_per_1m: '',
      output_cost_per_1m: '',
      per_request_cost: '',
      effective_from: new Date().toISOString().split('T')[0]
    });
    setEditingId(null);
    setShowForm(true);
  };

  if (forbidden) {
    return (
      <AdminGate backTo="/settings" />
    );
  }

  if (loading) {
    return (
      <PageLoading message="Loading pricing data..." />
    );
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      <PageHeader
        title="Pricing Management"
        subtitle="Configure LLM pricing for cost tracking"
        backTo="/settings"
        backLabel="Back to settings"
        actions={
          <Link
            to="/admin/costs"
            className="btn-clay-secondary btn-clay-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Cost Dashboard
          </Link>
        }
      />

      <main className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {/* Pending Pricing Changes (held by auto-sync for review) */}
        {pendingPricing.length > 0 && (
          <section className="mb-8">
            <div className="card-clay-sm border-l-4 border-l-fun-peach bg-fun-peach/10 mb-4">
              <div className="flex items-start">
                <span className="text-2xl mr-3">🔔</span>
                <div>
                  <h3 className="font-display font-semibold text-heading">Pricing Changes Awaiting Review</h3>
                  <p className="text-sm text-secondary-text mt-1">
                    The auto-sync detected these changes but held them because they exceed the safety guardrail. Approve to apply, or reject to dismiss.
                  </p>
                </div>
              </div>
            </div>
            <div className="card-clay overflow-hidden">
              <table className="min-w-full divide-y divide-clay-border">
                <thead className="bg-clay-bg">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Input $/1M</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Output $/1M</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-clay-border">
                  {pendingPricing.map((item) => (
                    <tr key={item.id} className="bg-fun-peach/10">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading capitalize">{item.provider}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-text font-mono">{item.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right font-mono">
                        <span className="text-secondary-text">{item.old_input_per_1m ?? '—'}</span>
                        <span className="mx-1">→</span>
                        <span className="font-semibold">{item.new_input_per_1m}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right font-mono">
                        <span className="text-secondary-text">{item.old_output_per_1m ?? '—'}</span>
                        <span className="mx-1">→</span>
                        <span className="font-semibold">{item.new_output_per_1m}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right space-x-3">
                        <button
                          onClick={() => handleApproveProposal(item.id)}
                          disabled={resolvingId === item.id}
                          className="btn-clay-ghost btn-clay-sm"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleRejectProposal(item.id)}
                          disabled={resolvingId === item.id}
                          className="btn-clay-ghost btn-clay-sm"
                        >
                          Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Unknown Pricing Alert */}
        {unknownPricing.length > 0 && (
          <section className="mb-8">
            <div className="card-clay-sm border-l-4 border-l-fun-peach bg-fun-peach/10 mb-4">
              <div className="flex items-start">
                <span className="text-2xl mr-3">⚠️</span>
                <div>
                  <h3 className="font-display font-semibold text-heading">Models Missing Pricing</h3>
                  <p className="text-sm text-secondary-text mt-1">
                    Add pricing for these models to enable cost tracking.
                  </p>
                </div>
              </div>
            </div>
            <div className="card-clay overflow-hidden">
              <table className="min-w-full divide-y divide-clay-border">
                <thead className="bg-clay-bg">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Requests</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-clay-border">
                  {unknownPricing.map((item, idx) => (
                    <tr key={idx} className="bg-fun-peach/10">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading capitalize">{item.provider}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-text font-mono">{item.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right">{item.count.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleAddFromUnknown(item)}
                          className="btn-clay-ghost btn-clay-sm"
                        >
                          Add Pricing
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Add/Edit Form + Pricing Table */}
        <section className="mb-8">
          <PricingForm
            showForm={showForm}
            setShowForm={setShowForm}
            editingId={editingId}
            formData={formData}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            onReset={resetForm}
            saving={saving}
          />

          {/* Pricing Table */}
          <div className="card-clay overflow-hidden">
            {pricing.length > 0 ? (
              <table className="min-w-full divide-y divide-clay-border">
                <thead className="bg-clay-bg">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-text uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Input/1M</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Output/1M</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Per Request</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Effective From</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-text uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-clay-border">
                  {pricing.map((item) => (
                    <tr key={item.id} className="hover:bg-clay-bg transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading capitalize">{item.provider}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-text font-mono">{item.model}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right">
                        {item.input_cost_per_1m ? `$${item.input_cost_per_1m}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right">
                        {item.output_cost_per_1m ? `$${item.output_cost_per_1m}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-heading text-right">
                        {item.per_request_cost ? `$${item.per_request_cost}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-text text-right">{item.effective_from}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                        <button
                          onClick={() => handleEdit(item)}
                          className="btn-clay-ghost btn-clay-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="btn-clay-ghost btn-clay-sm text-fun-coral"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-secondary-text text-center italic py-8">No pricing configured. Add pricing to start tracking costs.</p>
            )}
          </div>
        </section>

        {/* Recalculate Section */}
        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-heading mb-4">Recalculate Costs</h2>
          <div className="card-clay p-6">
            <p className="text-sm text-secondary-text mb-4">
              Recalculate costs for existing usage logs using current pricing. Useful after adding or updating pricing for a model.
            </p>
            <form onSubmit={handleRecalculate} className="flex flex-wrap items-end gap-4">
              <div>
                <label htmlFor="recalc-start-date" className="block text-sm font-medium text-body-text mb-1">Start Date</label>
                <input
                  id="recalc-start-date"
                  type="date"
                  value={recalcStartDate}
                  onChange={(e) => setRecalcStartDate(e.target.value)}
                  required
                  className="input-clay input-clay-sm"
                />
              </div>
              <div>
                <label htmlFor="recalc-end-date" className="block text-sm font-medium text-body-text mb-1">End Date</label>
                <input
                  id="recalc-end-date"
                  type="date"
                  value={recalcEndDate}
                  onChange={(e) => setRecalcEndDate(e.target.value)}
                  required
                  className="input-clay input-clay-sm"
                />
              </div>
              <button
                type="submit"
                disabled={recalculating}
                className="btn-clay-secondary"
              >
                {recalculating ? 'Recalculating...' : 'Recalculate'}
              </button>
            </form>
            {recalcResult && (
              <div className="mt-4 p-4 bg-fun-mint/20 border-l-4 border-fun-mint rounded-r-clay">
                <p className="text-sm text-heading">
                  Processed {recalcResult.total_processed.toLocaleString()} logs.
                  Updated {recalcResult.updated_count.toLocaleString()} with new costs.
                  {recalcResult.still_unknown > 0 && (
                    <span className="text-fun-peach font-medium"> {recalcResult.still_unknown.toLocaleString()} still missing pricing.</span>
                  )}
                  {recalcResult.total_cost_change !== 0 && (
                    <span> Total cost change: ${recalcResult.total_cost_change.toFixed(2)}</span>
                  )}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Pricing Reference */}
        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-heading mb-4">Pricing Reference</h2>
          <div className="card-clay p-6">
            <p className="text-sm text-secondary-text mb-4">Common model pricing (per 1M tokens, as of 2024):</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-clay-border">
                    <th className="py-2 text-left font-medium text-body-text">Provider</th>
                    <th className="py-2 text-left font-medium text-body-text">Model</th>
                    <th className="py-2 text-right font-medium text-body-text">Input</th>
                    <th className="py-2 text-right font-medium text-body-text">Output</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-clay-border">
                  <tr><td className="py-2">OpenAI</td><td className="font-mono">gpt-4o-mini</td><td className="text-right">$0.15</td><td className="text-right">$0.60</td></tr>
                  <tr><td className="py-2">OpenAI</td><td className="font-mono">text-embedding-3-small</td><td className="text-right">$0.02</td><td className="text-right">-</td></tr>
                  <tr><td className="py-2">Gemini</td><td className="font-mono">gemini-3-flash-preview</td><td className="text-right">$0.10</td><td className="text-right">$0.40</td></tr>
                  <tr><td className="py-2">Gemini</td><td className="font-mono">gemini-2.0-flash</td><td className="text-right">$0.075</td><td className="text-right">$0.30</td></tr>
                  <tr><td className="py-2">Anthropic</td><td className="font-mono">claude-3-haiku-20240307</td><td className="text-right">$0.25</td><td className="text-right">$1.25</td></tr>
                  <tr><td className="py-2">Vertex AI</td><td className="font-mono">multimodalembedding@001</td><td className="text-right" colSpan="2">~$0.0001/image</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PricingManagement;
